/**
 * This is the starting point of your application.
 * Feature Hub Apps will use this file to bootstrap the app.
 */
import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  ApolloProvider,
  ApolloClient,
  InMemoryCache,
  ApolloLink,
  createHttpLink,
} from '@apollo/client';
import { concat } from '@apollo/client/link/core';
import { I18nextProvider } from 'react-i18next';
import type { GfaServiceConfigProviderV1 } from '@volkswagen-onehub/gfa-service-config-provider';
import type { TrackingServiceV2 } from '@oneaudi/audi-tracking-service';
import type { ContentServiceV1 as ContentService } from '@volkswagen-onehub/audi-content-service';
import { ThemeProvider } from '@oneaudi/unified-web-common';
import { getGraphqlGatewayUrl } from './hooks/getGraphqlGatewayUrl';
import VehicleProvider from './components/VehicleProvider';
import i18n from '../i18n';
import HeaderBackBar from './components/HeaderBackBar';
import ErrorBoundary from './components/ErrorBoundary';
import { IS_TIER3_PAGE, IS_MOCK } from './constants';

interface FeatureAppProps {
  configProvider?: GfaServiceConfigProviderV1;
  trackingService?: TrackingServiceV2;
  contentService?: ContentService;
}

const correlationIdLink = new ApolloLink((operation, forward) => {
  operation.setContext(({ headers = {} }) => ({
    headers: {
      ...headers,
      'x-correlation-id': uuidv4(),
    },
  }));

  return forward(operation);
});

const httpLink = createHttpLink({
  uri: getGraphqlGatewayUrl(),
});

export const apolloClient = new ApolloClient({
  link: concat(correlationIdLink, httpLink),
  cache: new InMemoryCache(),
  name: __FEATURE_APP_NAME__,
  version: __FEATURE_APP_VERSION__,
});

const FeatureApp: React.FC<FeatureAppProps> = ({ ...inherited }: FeatureAppProps) => {
  const { trackingService } = inherited;

  React.useEffect(() => {
    if (!trackingService) return;
    const readyEvent = {
      event: {
        attributes: {
          componentName: __FEATURE_APP_NAME__,
          elementName: '',
          label: '',
          clickID: '',
          pos: '',
          targetURL: '',
          value: '',
        },
        eventInfo: {
          eventAction: 'feature_app_ready',
          eventName: `${__FEATURE_APP_NAME__} - Feature App ready`,
        },
      },
    };
    trackingService.track(readyEvent);
  }, [trackingService]);

  return (
    <I18nextProvider i18n={i18n}>
      <ErrorBoundary>
        <ThemeProvider>
          <ApolloProvider client={apolloClient}>
            <VehicleProvider isTier3={IS_TIER3_PAGE} isMock={IS_MOCK}>
              <div className="resetWrapper">
                <HeaderBackBar />
              </div>
            </VehicleProvider>
          </ApolloProvider>
        </ThemeProvider>
      </ErrorBoundary>
    </I18nextProvider>
  );
};

export default FeatureApp;
